/** Address fields the storefront renders; structural so `Address`, order delivery addresses and store addresses all fit. */
export type AddressParts = {
  street?: string | null;
  number?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  complement?: string | null;
};

/** The canonical two lines: "Rua, número" and "Bairro · Cidade/UF", plus the complement when there is one. */
export type AddressLines = { line1: string; line2: string; complement: string | null };

/** How much of the address to render: `full` returns lines, `compact` one string. */
export type AddressFormat = "full" | "compact";

const SEPARATOR = " · ";

function clean(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

function joinFilled(parts: string[], separator: string): string {
  return parts.filter((part) => part.length > 0).join(separator);
}

function toLines(address: AddressParts): AddressLines {
  const cityState = joinFilled([clean(address.city), clean(address.state)], "/");
  const complement = clean(address.complement);
  return {
    line1: joinFilled([clean(address.street), clean(address.number)], ", "),
    line2: joinFilled([clean(address.district), cityState], SEPARATOR),
    complement: complement.length > 0 ? complement : null,
  };
}

/** Formats an address the one storefront way; `compact` joins line 1 and line 2 with " · " for single-line rows. */
export function formatAddressLines(address: AddressParts, variant?: "full"): AddressLines;
export function formatAddressLines(address: AddressParts, variant: "compact"): string;
export function formatAddressLines(address: AddressParts, variant: AddressFormat = "full"): AddressLines | string {
  const lines = toLines(address);
  return variant === "compact" ? joinFilled([lines.line1, lines.line2], SEPARATOR) : lines;
}
